import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import clsx from 'clsx';
import ImportLeadsModal from '../components/ImportLeadsModal';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Types
interface Lead {
  id: string;
  customer_name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  project_type?: string;
  product_interest?: string;
  estimated_size?: string;
  estimated_budget?: number;
  lead_source?: string;
  status: string;
  notes?: string;
  activity_count?: number;
  next_follow_up?: string;
  last_activity_at?: string;
  created_at: string;
  converted_to_order_id?: string;
}

interface Activity {
  id: string;
  activity_type: string;
  duration_minutes?: number;
  summary?: string;
  next_follow_up?: string;
  activity_date: string;
  logged_by_first_name?: string;
  logged_by_last_name?: string;
}

interface LeadStats {
  total: number;
  by_status: Record<string, number>;
  conversion_rate: number;
  follow_ups_today: number;
  overdue_follow_ups: number;
}

// Constants
const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-500' },
  { value: 'contacted', label: 'Contacted', color: 'bg-purple-500' },
  { value: 'measured', label: 'Measured', color: 'bg-cyan-500' },
  { value: 'quoted', label: 'Quoted', color: 'bg-yellow-500' },
  { value: 'follow_up', label: 'Follow Up', color: 'bg-orange-500' },
  { value: 'won', label: 'Won', color: 'bg-green-500' },
  { value: 'lost', label: 'Lost', color: 'bg-red-500' },
  { value: 'converted', label: 'Converted', color: 'bg-emerald-600' },
];

const PROJECT_TYPES = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial', label: 'Commercial' },
];

const PRODUCT_INTERESTS = [
  { value: 'solar_screens', label: 'Solar Screens' },
  { value: 'premium_solar', label: 'Premium Solar' },
  { value: 'clear_vinyl', label: 'Clear Vinyl' },
  { value: 'insect_screens', label: 'Insect Screens' },
  { value: 'multiple', label: 'Multiple Products' },
];

const LEAD_SOURCES = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'door_hanger', label: 'Door Hanger' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'home_show', label: 'Home Show' },
  { value: 'other', label: 'Other' },
];

const ACTIVITY_TYPES = [
  { value: 'phone_call', label: 'Phone Call', icon: '📞' },
  { value: 'email', label: 'Email', icon: '✉️' },
  { value: 'text', label: 'Text', icon: '💬' },
  { value: 'site_visit', label: 'Site Visit', icon: '🏠' },
  { value: 'measurement', label: 'Measurement', icon: '📐' },
  { value: 'quote_sent', label: 'Quote Sent', icon: '💰' },
  { value: 'follow_up', label: 'Follow Up', icon: '🔔' },
  { value: 'note', label: 'Note', icon: '📝' },
];

// API Helper
async function apiRequest(endpoint: string, options: RequestInit = {}) {
  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  if (response.status === 204) return null;
  return response.json();
}

// Status Badge Component
function StatusBadge({ status }: { status: string }) {
  const statusInfo = STATUS_OPTIONS.find(s => s.value === status) || { label: status, color: 'bg-gray-500' };
  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded-full text-white', statusInfo.color)}>
      {statusInfo.label}
    </span>
  );
}

// Modal Component
function Modal({ isOpen, onClose, title, children, size = 'md' }: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/70" onClick={onClose} />
        <div className={clsx('relative bg-gray-800 rounded-xl shadow-2xl w-full', sizeClasses[size])}>
          <div className="border-b border-gray-700 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6 max-h-[80vh] overflow-y-auto">{children}</div>
        </div>
      </div>
    </div>
  );
}

// Lead Form Component
function LeadForm({ lead, onSave, onCancel }: {
  lead?: Lead | null;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    customer_name: lead?.customer_name || '',
    email: lead?.email || '',
    phone: lead?.phone || '',
    mobile: lead?.mobile || '',
    address: lead?.address || '',
    city: lead?.city || '',
    state: lead?.state || '',
    zip: lead?.zip || '',
    project_type: lead?.project_type || '',
    product_interest: lead?.product_interest || '',
    estimated_size: lead?.estimated_size || '',
    estimated_budget: lead?.estimated_budget || '',
    lead_source: lead?.lead_source || '',
    notes: lead?.notes || '',
    status: lead?.status || 'new',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customer_name.trim()) {
      setError('Customer name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        estimated_budget: form.estimated_budget ? Number(form.estimated_budget) : null,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-900/30 text-red-400 px-4 py-3 rounded-lg">{error}</div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Customer Name *</label>
        <input
          type="text"
          value={form.customer_name}
          onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
          required
          className="input-field w-full"
          placeholder="John Smith"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input-field w-full"
            placeholder="john@email.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="input-field w-full"
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Mobile</label>
        <input
          type="tel"
          value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          className="input-field w-full"
          placeholder="(555) 987-6543"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Address</label>
        <input
          type="text"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          className="input-field w-full"
          placeholder="123 Main St"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">City</label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="input-field w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">State</label>
          <input
            type="text"
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className="input-field w-full"
            maxLength={2}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">ZIP</label>
          <input
            type="text"
            value={form.zip}
            onChange={(e) => setForm({ ...form, zip: e.target.value })}
            className="input-field w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Project Type</label>
          <select
            value={form.project_type}
            onChange={(e) => setForm({ ...form, project_type: e.target.value })}
            className="input-field w-full"
          >
            <option value="">Select...</option>
            {PROJECT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Product Interest</label>
          <select
            value={form.product_interest}
            onChange={(e) => setForm({ ...form, product_interest: e.target.value })}
            className="input-field w-full"
          >
            <option value="">Select...</option>
            {PRODUCT_INTERESTS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Estimated Size</label>
          <input
            type="text"
            value={form.estimated_size}
            onChange={(e) => setForm({ ...form, estimated_size: e.target.value })}
            className="input-field w-full"
            placeholder="e.g., 500 sq ft or 8 openings"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Estimated Budget</label>
          <input
            type="number"
            value={form.estimated_budget}
            onChange={(e) => setForm({ ...form, estimated_budget: e.target.value })}
            onWheel={(e) => e.currentTarget.blur()}
            className="input-field w-full"
            placeholder="5000"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">How They Found You</label>
        <select
          value={form.lead_source}
          onChange={(e) => setForm({ ...form, lead_source: e.target.value })}
          className="input-field w-full"
        >
          <option value="">Select...</option>
          {LEAD_SOURCES.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {lead && (
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Status</label>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="input-field w-full"
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Notes</label>
        <textarea
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={3}
          className="input-field w-full"
          placeholder="Additional notes about this lead..."
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-300 hover:text-white">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50"
        >
          {saving ? 'Saving...' : lead ? 'Update Lead' : 'Add Lead'}
        </button>
      </div>
    </form>
  );
}

// Activity Form Component
function ActivityForm({ onSave, onCancel }: {
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    activity_type: 'phone_call',
    duration_minutes: '',
    summary: '',
    next_follow_up: '',
    activity_date: new Date().toISOString().slice(0, 16),
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Activity Type</label>
        <select
          value={form.activity_type}
          onChange={(e) => setForm({ ...form, activity_type: e.target.value })}
          className="input-field w-full"
        >
          {ACTIVITY_TYPES.map(t => (
            <option key={t.value} value={t.value}>{t.icon} {t.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Date/Time</label>
          <input
            type="datetime-local"
            value={form.activity_date}
            onChange={(e) => setForm({ ...form, activity_date: e.target.value })}
            className="input-field w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Duration (minutes)</label>
          <input
            type="number"
            value={form.duration_minutes}
            onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
            onWheel={(e) => e.currentTarget.blur()}
            className="input-field w-full"
            placeholder="15"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Summary</label>
        <textarea
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
          rows={3}
          className="input-field w-full"
          placeholder="What was discussed or accomplished..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Next Follow Up Date</label>
        <input
          type="date"
          value={form.next_follow_up}
          onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })}
          className="input-field w-full"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-300 hover:text-white">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 disabled:opacity-50"
        >
          {saving ? 'Logging...' : 'Log Activity'}
        </button>
      </div>
    </form>
  );
}

// Lead Detail View Component
function LeadDetailView({ lead, activities, onClose, onUpdate, onLogActivity, onConvert }: {
  lead: Lead;
  activities: Activity[];
  onClose: () => void;
  onUpdate: (data: any) => Promise<void>;
  onLogActivity: (data: any) => Promise<void>;
  onConvert: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const navigate = useNavigate();

  const getActivityIcon = (type: string) => {
    const activity = ACTIVITY_TYPES.find(a => a.value === type);
    return activity?.icon || '📋';
  };

  const getLeadCustomerData = () => ({
    leadId: lead.id,
    customerName: lead.customer_name,
    customerAddress: lead.address,
    customerCity: lead.city,
    customerState: lead.state,
    customerZip: lead.zip,
    customerPhone: lead.phone || lead.mobile,
    customerEmail: lead.email,
    projectName: lead.project_type || '',
  });

  const handleConvertToOrder = () => {
    sessionStorage.setItem('leadConvertData', JSON.stringify(getLeadCustomerData()));
    navigate('/orders/new');
  };

  const handleOpenPriceBuilder = () => {
    sessionStorage.setItem('leadToPriceBuilder', JSON.stringify(getLeadCustomerData()));
    navigate('/price-builder');
  };

  const handleOpenQuoteCalc = () => {
    // Quick estimate doesn't have customer fields, but we store for future use
    sessionStorage.setItem('leadConvertData', JSON.stringify(getLeadCustomerData()));
    navigate('/quote');
  };

  if (isEditing) {
    return (
      <LeadForm
        lead={lead}
        onSave={async (data) => {
          await onUpdate(data);
          setIsEditing(false);
        }}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white">{lead.customer_name}</h3>
          <StatusBadge status={lead.status} />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Edit
          </button>
          <button
            onClick={handleOpenPriceBuilder}
            className="px-3 py-1.5 text-sm bg-keder-yellow text-black rounded hover:bg-yellow-400 font-medium"
          >
            Price Builder
          </button>
          <button
            onClick={handleOpenQuoteCalc}
            className="px-3 py-1.5 text-sm bg-gray-700 text-white rounded hover:bg-gray-600"
          >
            Quick Estimate
          </button>
          {!lead.converted_to_order_id && (
            <button
              onClick={handleConvertToOrder}
              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-500"
            >
              Create Order
            </button>
          )}
        </div>
      </div>

      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-700/50 rounded-lg">
        {lead.email && (
          <div>
            <span className="text-gray-400 text-sm">Email:</span>
            <p className="text-white">{lead.email}</p>
          </div>
        )}
        {lead.phone && (
          <div>
            <span className="text-gray-400 text-sm">Phone:</span>
            <p className="text-white">{lead.phone}</p>
          </div>
        )}
        {lead.mobile && (
          <div>
            <span className="text-gray-400 text-sm">Mobile:</span>
            <p className="text-white">{lead.mobile}</p>
          </div>
        )}
        {lead.address && (
          <div className="col-span-2">
            <span className="text-gray-400 text-sm">Address:</span>
            <p className="text-white">
              {lead.address}
              {lead.city && `, ${lead.city}`}
              {lead.state && `, ${lead.state}`}
              {lead.zip && ` ${lead.zip}`}
            </p>
          </div>
        )}
      </div>

      {/* Project Details */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-700/50 rounded-lg">
        {lead.project_type && (
          <div>
            <span className="text-gray-400 text-sm">Project Type:</span>
            <p className="text-white capitalize">{lead.project_type}</p>
          </div>
        )}
        {lead.product_interest && (
          <div>
            <span className="text-gray-400 text-sm">Product Interest:</span>
            <p className="text-white">
              {PRODUCT_INTERESTS.find(p => p.value === lead.product_interest)?.label || lead.product_interest}
            </p>
          </div>
        )}
        {lead.estimated_size && (
          <div>
            <span className="text-gray-400 text-sm">Estimated Size:</span>
            <p className="text-white">{lead.estimated_size}</p>
          </div>
        )}
        {lead.estimated_budget && (
          <div>
            <span className="text-gray-400 text-sm">Estimated Budget:</span>
            <p className="text-white">${lead.estimated_budget.toLocaleString()}</p>
          </div>
        )}
        {lead.lead_source && (
          <div>
            <span className="text-gray-400 text-sm">Lead Source:</span>
            <p className="text-white">
              {LEAD_SOURCES.find(s => s.value === lead.lead_source)?.label || lead.lead_source}
            </p>
          </div>
        )}
      </div>

      {/* Notes */}
      {lead.notes && (
        <div className="p-4 bg-gray-700/50 rounded-lg">
          <span className="text-gray-400 text-sm">Notes:</span>
          <p className="text-white whitespace-pre-wrap">{lead.notes}</p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setShowActivityForm(true)}
          className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 flex items-center gap-1"
        >
          <span>📞</span> Log Activity
        </button>
        <a
          href={`tel:${lead.phone || lead.mobile}`}
          className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-500 flex items-center gap-1"
        >
          <span>📞</span> Call
        </a>
        {lead.email && (
          <a
            href={`mailto:${lead.email}`}
            className="px-3 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-500 flex items-center gap-1"
          >
            <span>✉️</span> Email
          </a>
        )}
      </div>

      {/* Activity Log Modal */}
      <Modal
        isOpen={showActivityForm}
        onClose={() => setShowActivityForm(false)}
        title="Log Activity"
        size="md"
      >
        <ActivityForm
          onSave={async (data) => {
            await onLogActivity(data);
            setShowActivityForm(false);
          }}
          onCancel={() => setShowActivityForm(false)}
        />
      </Modal>

      {/* Activity Timeline */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-3 flex items-center justify-between">
          Activity Timeline
          <button
            onClick={() => setShowActivityForm(true)}
            className="text-sm text-green-400 hover:text-green-300"
          >
            + Add Activity
          </button>
        </h4>
        {activities.length === 0 ? (
          <p className="text-gray-400 text-sm">No activities logged yet.</p>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => (
              <div key={activity.id} className="flex gap-3 p-3 bg-gray-700/30 rounded-lg">
                <span className="text-2xl">{getActivityIcon(activity.activity_type)}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white font-medium capitalize">
                      {activity.activity_type.replace(/_/g, ' ')}
                    </span>
                    {activity.duration_minutes && (
                      <span className="text-gray-400">({activity.duration_minutes} min)</span>
                    )}
                  </div>
                  {activity.summary && (
                    <p className="text-gray-300 text-sm mt-1">{activity.summary}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span>{format(new Date(activity.activity_date), 'MMM d, yyyy h:mm a')}</span>
                    {activity.logged_by_first_name && (
                      <span>by {activity.logged_by_first_name} {activity.logged_by_last_name}</span>
                    )}
                    {activity.next_follow_up && (
                      <span className="text-orange-400">Follow up: {format(new Date(activity.next_follow_up), 'MMM d')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Main Page Component
export default function MyLeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [leadActivities, setLeadActivities] = useState<Activity[]>([]);

  // Follow-ups
  const [todayFollowUps, setTodayFollowUps] = useState<Lead[]>([]);
  const [overdueFollowUps, setOverdueFollowUps] = useState<Lead[]>([]);

  const loadLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortOrder) params.append('sort_order', sortOrder);

      const data = await apiRequest(`/dealer-leads?${params.toString()}`);
      setLeads(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await apiRequest('/dealer-leads/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const loadFollowUps = async () => {
    try {
      const [today, overdue] = await Promise.all([
        apiRequest('/dealer-leads/follow-ups/today'),
        apiRequest('/dealer-leads/follow-ups/overdue'),
      ]);
      setTodayFollowUps(today || []);
      setOverdueFollowUps(overdue || []);
    } catch (err) {
      console.error('Failed to load follow-ups:', err);
    }
  };

  const loadLeadActivities = async (leadId: string) => {
    try {
      const data = await apiRequest(`/dealer-leads/${leadId}/activities`);
      setLeadActivities(data || []);
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  };

  useEffect(() => {
    loadLeads();
    loadStats();
    loadFollowUps();
  }, [statusFilter, sortBy, sortOrder]);

  useEffect(() => {
    if (selectedLead) {
      loadLeadActivities(selectedLead.id);
    }
  }, [selectedLead]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadLeads();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCreateLead = async (data: any) => {
    // Remove status from data when creating - it defaults to 'new' on backend
    const { status, ...createData } = data;
    await apiRequest('/dealer-leads', {
      method: 'POST',
      body: JSON.stringify(createData),
    });
    setShowAddModal(false);
    loadLeads();
    loadStats();
  };

  const handleUpdateLead = async (data: any) => {
    if (!selectedLead) return;
    await apiRequest(`/dealer-leads/${selectedLead.id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    const updated = await apiRequest(`/dealer-leads/${selectedLead.id}`);
    setSelectedLead(updated);
    loadLeads();
    loadStats();
  };

  const handleLogActivity = async (data: any) => {
    if (!selectedLead) return;
    await apiRequest(`/dealer-leads/${selectedLead.id}/activities`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    loadLeadActivities(selectedLead.id);
    loadFollowUps();
  };

  const handleDeleteLead = async (id: string) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    await apiRequest(`/dealer-leads/${id}`, { method: 'DELETE' });
    if (selectedLead?.id === id) setSelectedLead(null);
    loadLeads();
    loadStats();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">My Leads</h1>
          <p className="text-gray-400">Track and manage your customer prospects</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2 border border-gray-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Lead
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-2xl font-bold text-white">{stats.total}</p>
            <p className="text-gray-400 text-sm">Total Leads</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-2xl font-bold text-green-400">{stats.conversion_rate}%</p>
            <p className="text-gray-400 text-sm">Conversion Rate</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <p className="text-2xl font-bold text-blue-400">{stats.by_status?.new || 0}</p>
            <p className="text-gray-400 text-sm">New Leads</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-orange-500/30">
            <p className="text-2xl font-bold text-orange-400">{stats.follow_ups_today}</p>
            <p className="text-gray-400 text-sm">Follow-ups Today</p>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-red-500/30">
            <p className="text-2xl font-bold text-red-400">{stats.overdue_follow_ups}</p>
            <p className="text-gray-400 text-sm">Overdue</p>
          </div>
        </div>
      )}

      {/* Follow-up Alerts */}
      {(todayFollowUps.length > 0 || overdueFollowUps.length > 0) && (
        <div className="mb-6 space-y-3">
          {overdueFollowUps.length > 0 && (
            <div className="p-4 bg-red-900/30 border border-red-500 rounded-lg">
              <h3 className="text-red-400 font-semibold mb-2">Overdue Follow-ups</h3>
              <div className="flex flex-wrap gap-2">
                {overdueFollowUps.map(lead => (
                  <button
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className="px-3 py-1 bg-red-800/50 text-red-200 text-sm rounded hover:bg-red-700/50"
                  >
                    {lead.customer_name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {todayFollowUps.length > 0 && (
            <div className="p-4 bg-orange-900/30 border border-orange-500 rounded-lg">
              <h3 className="text-orange-400 font-semibold mb-2">Follow-ups Due Today</h3>
              <div className="flex flex-wrap gap-2">
                {todayFollowUps.map(lead => (
                  <button
                    key={lead.id}
                    onClick={() => setSelectedLead(lead)}
                    className="px-3 py-1 bg-orange-800/50 text-orange-200 text-sm rounded hover:bg-orange-700/50"
                  >
                    {lead.customer_name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name, phone, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field w-full"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-full md:w-48"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="input-field w-full md:w-48"
        >
          <option value="created_at">Date Added</option>
          <option value="last_activity">Last Activity</option>
          <option value="customer_name">Name</option>
        </select>
        <button
          onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
        </button>
      </div>

      {/* Leads List */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading leads...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-400">{error}</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-400 mb-4">No leads found</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500"
            >
              Add Your First Lead
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Follow-up</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Added</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {leads.map(lead => (
                  <tr key={lead.id} className="hover:bg-gray-700/30">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedLead(lead)}
                        className="text-left"
                      >
                        <p className="text-white font-medium hover:text-green-400">{lead.customer_name}</p>
                        {lead.city && lead.state && (
                          <p className="text-gray-400 text-sm">{lead.city}, {lead.state}</p>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {lead.phone && <p className="text-gray-300">{lead.phone}</p>}
                      {lead.email && <p className="text-gray-400 text-xs">{lead.email}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {PRODUCT_INTERESTS.find(p => p.value === lead.product_interest)?.label || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={lead.status} />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {lead.next_follow_up ? (
                        <span className={clsx(
                          new Date(lead.next_follow_up) < new Date() ? 'text-red-400' : 'text-orange-400'
                        )}>
                          {format(new Date(lead.next_follow_up), 'MMM d')}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {format(new Date(lead.created_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedLead(lead)}
                          className="text-gray-400 hover:text-white"
                          title="View"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-gray-400 hover:text-red-400"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Lead" size="lg">
        <LeadForm onSave={handleCreateLead} onCancel={() => setShowAddModal(false)} />
      </Modal>

      {/* Lead Detail Modal */}
      <Modal
        isOpen={!!selectedLead}
        onClose={() => setSelectedLead(null)}
        title="Lead Details"
        size="xl"
      >
        {selectedLead && (
          <LeadDetailView
            lead={selectedLead}
            activities={leadActivities}
            onClose={() => setSelectedLead(null)}
            onUpdate={handleUpdateLead}
            onLogActivity={handleLogActivity}
            onConvert={() => {}}
          />
        )}
      </Modal>

      {/* Import Leads Modal */}
      <ImportLeadsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={() => {
          loadLeads();
          loadStats();
        }}
      />
    </div>
  );
}
